import React from "react";
import cx from "classnames";
import _ from "lodash";
import { Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useExperiencesTemplates, useUpdateTemplateVisibility } from "src/query/experiences/templates";
import Toast from "src/components/toast";
import { TagList } from "src/components/tagList";
import Thumbnail from "src/components/thumbnail";
import InfiniteScroll from "src/components/infiniteScroll";
import { StoryboardType } from "src/types/experiences/storyboardSettings/storyboard";
import { Template } from "src/types/experiences/templates";
import { Media } from "src/types/media";
import {
  ALL_FILTER_VALUE,
  CATEGORY_ALL_LABEL,
  INDUSTRY_ALL_LABEL,
  TEMPLATE_SORT_FIELDS,
  TEMPLATE_SORT_OPTIONS,
  TEMPLATE_SORT_ORDERS,
  TemplateSortOrder,
} from "src/pages/experiences/constants/storyboard";

import styles from "./createStoryboardDialog.module.scss";
import { FilterDropdown } from "./FilterDropdown";
import { SortByAction } from "./SortByAction";
import { STORYBOARD_TYPES } from "../../types";

interface Option {
  value: string;
  label: string;
}

function buildTemplateMedia(
  icon?: { id?: string; url?: string } | null,
): Media | undefined {
  if (!icon?.url) return undefined;
  return { id: icon.id ?? "", type: "image", key: icon.url };
}

interface TemplateCardProps {
  template: Template;
  onEdit: TemplatePanelProps["onEdit"];
  onPreview: TemplatePanelProps["onEdit"];
  superAdmin: boolean;
  onToggleVisibility: TemplatePanelProps["onEdit"];
  isTogglingVisibility: boolean;
  pendingEnabled?: boolean;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onEdit,
  onPreview,
  superAdmin,
  onToggleVisibility,
  isTogglingVisibility,
  pendingEnabled,
}) => {
  const tags = [...(template.categories ?? [])];
  const media = buildTemplateMedia(template.icon);
  const isEnabled =
    pendingEnabled !== undefined && !isTogglingVisibility
      ? pendingEnabled
      : template.enabled !== false;

  return (
    <div
      className={cx(styles.templateCard, {
        [styles.templateCardDisabled]: superAdmin && !isEnabled,
      })}
    >
      <div className={styles.templateThumbnail}>
        <Thumbnail
          media={media}
          defaultImage="/images/collections/background.jpeg"
          width={600}
        />
        <div className={styles.templateOverlay}>
          <button
            className={styles.editBtn}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(template);
            }}
          >
            <i className={cx("paperflite-edit", styles.editIcon)} />
            edit
          </button>
          <button
            className={styles.previewBtn}
            onClick={(e) => {
              e.stopPropagation();
              onPreview(template);
            }}
          >
            <i className={cx("paperflite-eye", styles.previewIcon)} />
            preview
          </button>
        </div>
        {superAdmin && (
          <div
            className={cx(styles.templateVisibilityToggle, {
              [styles.templateVisibilityToggleVisible]:
                !isEnabled || isTogglingVisibility,
              [styles.templateVisibilityToggleActive]: isEnabled,
            })}
            onClick={(e) => e.stopPropagation()}
          >
            {isTogglingVisibility ? (
              <span className={styles.visibilitySwitchSpinner} />
            ) : (
              <div
                className={cx(styles.visibilitySwitch, {
                  [styles.visibilitySwitchActive]: isEnabled,
                })}
                onClick={() => onToggleVisibility(template)}
              >
                <span className={styles.visibilitySwitchTrack} />
                <span className={styles.visibilitySwitchThumb} />
              </div>
            )}
            <span className={styles.visibilitySwitchLabel}>
              {isEnabled ? "Enabled" : "Disabled"}
            </span>
          </div>
        )}
      </div>
      <div className={styles.templateCardBottom}>
        <p className={styles.templateName}>{template.name}</p>
        {tags.length > 0 && (
          <div className={styles.templateTags}>
            <TagList
              tags={tags}
              maxVisible={2}
              className={styles.templateTag}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const PlaceholderTemplateCard: React.FC = () => (
  <div className={cx(styles.templateCard, styles.placeholder)}>
    <div className={cx(styles.templateThumbnail, styles.block)} />
    <div className={styles.templateCardBottom}>
      <div className={cx(styles.nameBlock, styles.block)} />
      <div className={cx(styles.tagsBlock, styles.block)} />
    </div>
  </div>
);

interface TemplatePanelProps {
  contentType: StoryboardType | null;
  onEdit: (tpl: Template) => void;
}

export const TemplatePanel: React.FC<TemplatePanelProps> = ({
  contentType,
  onEdit,
}) => {
  const handlePreview = (tpl: Template) => {
    if (tpl.url) window.open(tpl.url, "_blank");
  };

  const superAdmin = useSelector((state: any) =>
    _.get(state, "experiences.workspaces.workspace.item.superAdmin", false),
  );
  const {
    mutate: updateVisibility,
    isPending: isTogglingVisibility,
    variables: toggleVisibilityVariables,
  } = useUpdateTemplateVisibility();
  const togglingTemplateId = isTogglingVisibility
    ? (toggleVisibilityVariables as { template: string } | undefined)?.template
    : undefined;
  const [optimisticEnabled, setOptimisticEnabled] = React.useState<
    Record<string, boolean>
  >({});

  const handleToggleVisibility = (tpl: Template) => {
    const newEnabled = tpl.enabled === false;
    setOptimisticEnabled((prev) => ({ ...prev, [tpl.id]: newEnabled }));
    updateVisibility(
      { template: tpl.id, enabled: newEnabled },
      {
        onError: () => {
          setOptimisticEnabled((prev) => {
            const next = { ...prev };
            delete next[tpl.id];
            return next;
          });
          toast(
            <Toast.Failure message="Failed to update template visibility" />,
            { autoClose: 3000 },
          );
        },
      },
    );
  };
  const [sortState, setSortState] = React.useState<{
    sortBy: string;
    orderBy: TemplateSortOrder;
  }>({
    sortBy: TEMPLATE_SORT_FIELDS.CREATED_DATE,
    orderBy: TEMPLATE_SORT_ORDERS.DESC,
  });
  const [industryFilter, setIndustryFilter] = React.useState<Option>({
    value: ALL_FILTER_VALUE,
    label: INDUSTRY_ALL_LABEL,
  });
  const [categoryFilter, setCategoryFilter] = React.useState<Option>({
    value: ALL_FILTER_VALUE,
    label: CATEGORY_ALL_LABEL,
  });
  const [showCategoryDropdown, setShowCategoryDropdown] = React.useState(false);

  const { data, isLoading, isFetchingNextPage, hasNextPage, loadMore } =
    useExperiencesTemplates({
      type: contentType ?? ALL_FILTER_VALUE,
      category: categoryFilter.value,
      industry: industryFilter.value,
      sort: sortState.sortBy,
      order: sortState.orderBy,
    });

  const templates: Template[] = React.useMemo(
    () => data?.pages.flatMap((page: any) => page.records) ?? [],
    [data?.pages],
  );

  React.useEffect(() => {
    setOptimisticEnabled((prev) => {
      if (_.isEmpty(prev)) return prev;
      const next = { ...prev };
      let changed = false;
      templates.forEach((tpl) => {
        if (
          tpl.id in next &&
          (tpl.enabled !== false) === next[tpl.id]
        ) {
          delete next[tpl.id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [templates]);

  const { data: allTypesData } = useExperiencesTemplates({
    type: ALL_FILTER_VALUE,
    category: ALL_FILTER_VALUE,
    industry: ALL_FILTER_VALUE,
    sort: TEMPLATE_SORT_FIELDS.CREATED_DATE,
    order: TEMPLATE_SORT_ORDERS.DESC,
  });

  const allTypesTemplates: Template[] = React.useMemo(
    () => allTypesData?.pages.flatMap((page: any) => page.records) ?? [],
    [allTypesData?.pages],
  );

  const [categoryOptions, setCategoryOptions] = React.useState<Option[]>([
    { value: ALL_FILTER_VALUE, label: CATEGORY_ALL_LABEL },
  ]);
  const [industryOptions, setIndustryOptions] = React.useState<Option[]>([
    { value: ALL_FILTER_VALUE, label: INDUSTRY_ALL_LABEL },
  ]);

  React.useEffect(() => {
    const feed = [...allTypesTemplates, ...templates];

    const scrapedCategories = _.flatMap(feed, (t) => t.categories ?? [])
      .filter((c): c is string => !!c && c.length !== 0)
      .map((c) => ({ value: c, label: c }));
    setCategoryOptions((prev) => {
      const merged = _.uniqBy([...prev, ...scrapedCategories], "value");
      return merged.length === prev.length ? prev : merged;
    });

    const scrapedIndustries = _.flatMap(feed, (t) => t.industries ?? [])
      .filter((i): i is string => !!i && i.length !== 0)
      .map((i) => ({ value: i, label: i }));
    setIndustryOptions((prev) => {
      const merged = _.uniqBy([...prev, ...scrapedIndustries], "value");
      return merged.length === prev.length ? prev : merged;
    });
  }, [allTypesTemplates, templates]);

  return (
    <div className={styles.templatePanel}>
      <div className={styles.templateHeader}>
        {contentType !== STORYBOARD_TYPES.RESOURCE_CENTER ? (
          <Dropdown
            className={styles.allTemplatesDropdown}
            onToggle={(isOpen) => setShowCategoryDropdown(isOpen)}
          >
            <Dropdown.Toggle>
              {categoryFilter.label}
              <i
                className={cx("paperflite-dropdown", {
                  [styles.openModal]: showCategoryDropdown,
                })}
              />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{
                modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
              }}
            >
              {categoryOptions.map((option) => {
                const isSelected = option.value === categoryFilter.value;
                return (
                  <Dropdown.Item
                    key={option.value || "__all__"}
                    active={isSelected}
                    onClick={() => {
                      setCategoryFilter(option);
                      setShowCategoryDropdown(false);
                    }}
                  >
                    <span>{option.label}</span>
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        ) : (
          <span className={styles.templatesLabel}>Templates</span>
        )}
        <div className={styles.filterChips}>
          <div className={styles.industryDropdown}>
            <FilterDropdown
              selected={industryFilter}
              options={industryOptions}
              onChange={setIndustryFilter}
            />
          </div>
          <SortByAction
            value={sortState}
            onChange={(v) =>
              setSortState({
                sortBy: v.sortBy,
                orderBy: v.orderBy as TemplateSortOrder,
              })
            }
            sortOptions={
              TEMPLATE_SORT_OPTIONS as { label: string; value: string }[]
            }
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.templateGrid}>
          {_.times(6).map((i) => (
            <PlaceholderTemplateCard key={i} />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className={styles.emptyState}>No templates found</div>
      ) : (
        <InfiniteScroll
          className={styles.templateGrid}
          loading={isFetchingNextPage}
          hasMore={!!hasNextPage}
          loadMore={loadMore}
          loader={() =>
            _.times(3).map((i) => <PlaceholderTemplateCard key={i} />)
          }
        >
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={onEdit}
              onPreview={handlePreview}
              superAdmin={superAdmin}
              onToggleVisibility={handleToggleVisibility}
              isTogglingVisibility={togglingTemplateId === template.id}
              pendingEnabled={optimisticEnabled[template.id]}
            />
          ))}
        </InfiniteScroll>
      )}
    </div>
  );
};
